import React, { useState, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Users, AlertCircle, CheckCircle, Check, X, AlertTriangle, Download, Loader2, ServerOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';
import { getLocalizedPath, getHomePath } from '../utils/routes';
import { useTranslations } from '../context/TranslationsContext';
import { useFitnet } from '../contexts/FitnetContext';
import PhonePrefixSelect from '../components/PhonePrefixSelect';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to calculate age from birth date
const calculateAge = (birthDate) => {
  if (!birthDate) return null;
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

// Minor Alert Component
const MinorAlert = ({ t, lang }) => (
  <motion.div 
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    className="mt-3 p-4 rounded-xl border-2 border-amber-500/50 bg-amber-500/10 backdrop-blur-sm"
  >
    <div className="flex items-start gap-3">
      <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <h4 className="font-bold text-amber-500 text-sm uppercase tracking-wide">
          {t('auth.minorAlert.title')}
        </h4>
        <p className="text-sm text-muted-foreground mt-1">
          {t('auth.minorAlert.description')}
        </p>
        <a
          href={`${API}/uploads/oswiadczenie-rodzica-opiekuna.pdf`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <Download className="w-4 h-4" />
          {t('auth.minorAlert.downloadBtn')}
        </a>
      </div>
    </div>
  </motion.div>
);

const LoginPage = () => {
  const { t: ti18n, i18n } = useTranslation();
  const { td: tCms } = useTranslations(['auth', 'common', 'errors']);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const lang = i18n.language;
  
  // Translation helper
  const t = (key, fallback = null) => {
    const cmsValue = tCms(key);
    if (cmsValue && cmsValue !== key) return cmsValue;
    const i18nVal = ti18n(key);
    if (i18nVal && i18nVal !== key) return i18nVal;
    return fallback || key;
  };

  // Helper for localized paths
  const lp = (key) => getLocalizedPath(key, lang);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const from = location.state?.from || getHomePath(lang);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(t('auth.invalidCredentials', 'Invalid email or password'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-28 sm:pt-32 pb-20 px-4 sm:px-6 md:px-12 flex items-center justify-center" data-testid="login-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="glass-card p-6 sm:p-8">
          <h1 className="text-2xl sm:text-3xl font-['Syne'] font-bold text-center mb-6 sm:mb-8">{t('auth.login')}</h1>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm text-center flex items-center justify-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@example.com"
                className="input-glass mt-2"
                required
                data-testid="login-email"
              />
            </div>

            <div>
              <Label htmlFor="password">{t('auth.password')}</Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pr-12 input-glass"
                  required
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <div className="text-right mt-1">
                <Link 
                  to={lp('forgotPassword')} 
                  className="text-sm text-primary hover:underline"
                  data-testid="forgot-password-link"
                >
                  {t('auth.forgotPassword', 'Forgot password?')}
                </Link>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
              data-testid="login-submit"
            >
              {loading ? t('common.loading') : t('auth.loginBtn')}
            </button>
          </form>

          <p className="mt-8 text-center text-sm text-muted-foreground">
            {t('auth.noAccount')}{' '}
            <Link to={lp('register')} className="text-primary hover:underline font-medium" data-testid="register-link">
              {t('auth.register')}
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

const RegisterPage = () => {
  const { t: ti18n, i18n } = useTranslation();
  const { td } = useTranslations();
  const navigate = useNavigate();
  const lang = i18n.language;
  const { isAvailable: isFitnetAvailable, loading: fitnetLoading } = useFitnet();
  
  // Translation helper
  const t = (key, fallback = null) => {
    const cmsValue = td(key);
    if (cmsValue && cmsValue !== key) return cmsValue;
    const i18nVal = ti18n(key);
    if (i18nVal && i18nVal !== key) return i18nVal;
    return fallback || key;
  };

  // Helper for localized paths
  const lp = (key) => getLocalizedPath(key, lang);

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [registeredUser, setRegisteredUser] = useState(null);
  const [token, setToken] = useState(null);

  // Step 1 - Main account
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    phoneCode: '+48',
    birthDate: '',
    gender: '',
    // 4 consents for FitNet integration + newsletter
    agreementTermsOfService: false,    // Regulamin Serwisu (required)
    agreementSkateparkRules: false,    // Regulamin Skateparku (required)
    agreementPrivacyPolicy: false,     // Polityka Prywatności (required)
    agreementMarketing: false,         // Zgoda Marketingowa (optional)
    agreementNewsletter: false         // Newsletter (optional)
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Password strength calculation
  const passwordStrength = useMemo(() => {
    const password = formData.password;
    if (!password) return { score: 0, label: '', color: '' };
    
    let score = 0;
    const checks = {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecial: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    };
    
    if (checks.minLength) score++;
    if (checks.hasUppercase) score++;
    if (checks.hasLowercase) score++;
    if (checks.hasNumber) score++;
    if (checks.hasSpecial) score++;
    
    let label, color;
    if (score <= 2) {
      label = t('auth.passwordWeak', 'Weak');
      color = 'bg-red-500';
    } else if (score <= 3) {
      label = t('auth.passwordMedium', 'Medium');
      color = 'bg-yellow-500';
    } else if (score <= 4) {
      label = t('auth.passwordGood', 'Good');
      color = 'bg-blue-500';
    } else {
      label = t('auth.passwordStrong', 'Strong');
      color = 'bg-green-500';
    }
    
    return { score, label, color, checks };
  }, [formData.password, t]);

  const passwordsMatch = formData.password && formData.confirmPassword && formData.password === formData.confirmPassword;
  const passwordMismatch = formData.confirmPassword && formData.password !== formData.confirmPassword;

  // Step 2 - Subaccounts
  const [subaccounts, setSubaccounts] = useState([]);
  const [newSubaccount, setNewSubaccount] = useState({
    firstName: '',
    lastName: '',
    birthDate: '',
    phone: '',
    gender: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleStep1Submit = async (e) => {
    e.preventDefault();
    
    // Validate required consents (3 are required, marketing is optional)
    if (!formData.agreementTermsOfService || !formData.agreementSkateparkRules || !formData.agreementPrivacyPolicy) {
      setError(t('auth.acceptAllRequired'));
      return;
    }
    
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password) {
      setError(t('auth.fillRequiredFields', 'Fill in all required fields'));
      return;
    }

    // Validate password confirmation
    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.passwordsNotMatch', 'Passwords do not match'));
      return;
    }

    // Validate password strength (minimum requirements)
    if (passwordStrength.score < 4) {
      setError(t('auth.passwordTooWeak', 'Password must contain min. 8 characters, uppercase, digit, and special character'));
      return;
    }

    if (!formData.gender) {
      setError(t('auth.selectGender', 'Select gender'));
      return;
    }

    setError('');
    setLoading(true);

    try {
      // Map gender to sex for backend (FitNet uses sex field)
      const payload = {
        ...formData,
        sex: formData.gender,
        language: lang // Add current language for email localization
      };
      delete payload.confirmPassword; // Don't send confirmPassword to backend
      
      // Use the new pending registration endpoint with email verification
      const response = await axios.post(`${API}/auth/register-pending`, payload);
      
      if (response.data.pendingVerification) {
        // Redirect to pending verification page
        const pendingPath = lang === 'en' ? 'pending-verification' : 'oczekuje-na-weryfikacje';
        navigate(`/${lang}/${pendingPath}?email=${encodeURIComponent(formData.email)}`);
      } else {
        // Fallback to old behavior if verification is disabled
        setRegisteredUser(response.data.user);
        setToken(response.data.token);
        localStorage.setItem('avepark_token', response.data.token);
        setStep(2);
      }
    } catch (err) {
      console.error('Registration error:', err);
      // Map backend error messages to translation keys
      const backendError = err.response?.data?.detail || err.response?.data?.error;
      const errorMap = {
        'User already exists': t('errors.emailExists', 'Ten email jest już zarejestrowany'),
        'Email is already registered': t('errors.emailExists', 'Ten email jest już zarejestrowany'),
        'Missing required fields': t('auth.fillRequiredFields', 'Wypełnij wszystkie wymagane pola'),
        'Required consents must be accepted': t('auth.acceptAllRequired', 'Musisz zaakceptować wszystkie wymagane zgody'),
        'Registration failed': t('auth.registrationFailed', 'Rejestracja nie powiodła się'),
      };
      setError(errorMap[backendError] || backendError || t('auth.registrationFailed', 'Registration failed'));
    } finally {
      setLoading(false);
    }
  };

  const addSubaccount = async () => {
    if (!newSubaccount.firstName || !newSubaccount.lastName || !newSubaccount.birthDate) {
      setError(t('auth.fillPersonData', 'Fill in first name, last name and birth date'));
      return;
    }

    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/subaccounts`, newSubaccount, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubaccounts([...subaccounts, response.data]);
      setNewSubaccount({ firstName: '', lastName: '', birthDate: '', phone: '', gender: '' });
    } catch (err) {
      setError(t('auth.addPersonFailed', 'Failed to add person'));
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = () => {
    setSuccess(true);
    setTimeout(() => {
      navigate('/account');
    }, 2000);
  };

  if (success) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-6 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center glass-card p-12 max-w-md"
        >
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold mb-4">
            {t('auth.registrationComplete', 'Registration complete!')}
          </h2>
          <p className="text-muted-foreground mb-4">
            {t('auth.accountCreatedInfo', 'Your account has been created and linked to the FitNet system.')}
          </p>
          {registeredUser && (
            <p className="text-sm text-primary">
              FitNet ID: {registeredUser.fitnetId || registeredUser.user_id}
            </p>
          )}
        </motion.div>
      </div>
    );
  }

  // Show loading state while checking FitNet status
  if (fitnetLoading) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-6 md:px-12 flex items-center justify-center" data-testid="register-page">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">{t('common.loading', 'Ładowanie...')}</p>
        </div>
      </div>
    );
  }

  // Show unavailable message when FitNet is down
  if (!isFitnetAvailable) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-6 md:px-12 flex items-center justify-center" data-testid="register-page">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-lg"
        >
          <div className="glass-card p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-orange-500/20 flex items-center justify-center">
              <ServerOff className="w-8 h-8 text-orange-400" />
            </div>
            <h1 className="text-2xl font-['Syne'] font-bold mb-4">{t('maintenance.registrationUnavailable', 'Rejestracja jest tymczasowo niedostępna')}</h1>
            <p className="text-muted-foreground mb-6">
              {t('maintenance.description', 'System rezerwacji jest chwilowo wyłączony. Przepraszamy za utrudnienia.')}
            </p>
            <Link 
              to={getHomePath(lang)} 
              className="btn-primary inline-block"
            >
              {t('maintenance.backToHome', 'Wróć do strony głównej')}
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 sm:pt-32 pb-20 px-4 sm:px-6 md:px-12 flex items-center justify-center" data-testid="register-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg"
      >
        <div className="glass-card p-5 sm:p-8">
          {/* Step Indicator */}
          <div className="flex items-center justify-center gap-3 sm:gap-4 mb-6 sm:mb-8">
            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-bold text-sm sm:text-base ${step >= 1 ? 'bg-primary' : 'bg-white/10'}`}>
              1
            </div>
            <div className={`w-12 sm:w-16 h-1 ${step >= 2 ? 'bg-primary' : 'bg-white/10'}`} />
            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-bold text-sm sm:text-base ${step >= 2 ? 'bg-primary' : 'bg-white/10'}`}>
              2
            </div>
          </div>

          {step === 1 ? (
            <>
              <h1 className="text-2xl sm:text-3xl font-['Syne'] font-bold text-center mb-2">{t('auth.register')}</h1>
              <p className="text-muted-foreground text-center text-sm sm:text-base mb-4 sm:mb-6">{t('auth.step1Title')}</p>
              <p className="text-xs text-center text-primary/70 mb-4 sm:mb-6">
                {t('auth.masterAccountInfo', 'This account is like a master key. It is not only for the rider, but for the person who will manage the profiles and pay for entries.')}
              </p>

              {error && (
                <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm text-center flex items-center justify-center gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  {error}
                </div>
              )}

              <form onSubmit={handleStep1Submit} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <Label>{t('auth.firstName')} *</Label>
                    <Input
                      value={formData.firstName}
                      onChange={(e) => handleChange('firstName', e.target.value)}
                      className="input-glass mt-2"
                      required
                      data-testid="register-firstname"
                    />
                  </div>
                  <div>
                    <Label>{t('auth.lastName')} *</Label>
                    <Input
                      value={formData.lastName}
                      onChange={(e) => handleChange('lastName', e.target.value)}
                      className="input-glass mt-2"
                      required
                      data-testid="register-lastname"
                    />
                  </div>
                </div>

                <div>
                  <Label>{t('auth.email')} *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="input-glass mt-2"
                    required
                    data-testid="register-email"
                  />
                </div>

                {/* Password with strength indicator */}
                <div>
                  <Label>{t('auth.password')} *</Label>
                  <div className="relative mt-2">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => handleChange('password', e.target.value)}
                      className="input-glass pr-12"
                      required
                      data-testid="register-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  
                  {/* Password strength indicator */}
                  {formData.password && (
                    <div className="mt-3 space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                          <div 
                            className={`h-full transition-all duration-300 ${passwordStrength.color}`}
                            style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                          />
                        </div>
                        <span className={`text-xs font-medium ${
                          passwordStrength.score <= 2 ? 'text-red-400' : 
                          passwordStrength.score <= 3 ? 'text-yellow-400' : 
                          passwordStrength.score <= 4 ? 'text-blue-400' : 'text-green-400'
                        }`}>
                          {passwordStrength.label}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-1 text-xs">
                        <div className={`flex items-center gap-1 ${passwordStrength.checks?.minLength ? 'text-green-400' : 'text-muted-foreground'}`}>
                          {passwordStrength.checks?.minLength ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                          {t('auth.min8chars', 'Min. 8 characters')}
                        </div>
                        <div className={`flex items-center gap-1 ${passwordStrength.checks?.hasUppercase ? 'text-green-400' : 'text-muted-foreground'}`}>
                          {passwordStrength.checks?.hasUppercase ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                          {t('auth.uppercase', 'Uppercase')}
                        </div>
                        <div className={`flex items-center gap-1 ${passwordStrength.checks?.hasNumber ? 'text-green-400' : 'text-muted-foreground'}`}>
                          {passwordStrength.checks?.hasNumber ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                          {t('auth.number', 'Number')}
                        </div>
                        <div className={`flex items-center gap-1 ${passwordStrength.checks?.hasSpecial ? 'text-green-400' : 'text-muted-foreground'}`}>
                          {passwordStrength.checks?.hasSpecial ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                          {t('auth.specialChar', 'Special char')}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <Label>{t('auth.confirmPassword', 'Confirm password')} *</Label>
                  <div className="relative mt-2">
                    <Input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) => handleChange('confirmPassword', e.target.value)}
                      className={`input-glass pr-12 ${passwordMismatch ? 'border-red-500' : ''} ${passwordsMatch ? 'border-green-500' : ''}`}
                      required
                      data-testid="register-confirm-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {passwordMismatch && (
                    <p className="mt-1 text-xs text-red-400 flex items-center gap-1">
                      <X className="w-3 h-3" />
                      {t('auth.passwordsNotMatch', 'Passwords do not match')}
                    </p>
                  )}
                  {passwordsMatch && (
                    <p className="mt-1 text-xs text-green-400 flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      {t('auth.passwordsMatch', 'Passwords match')}
                    </p>
                  )}
                </div>

                <div>
                  <Label>{t('auth.phone')} *</Label>
                  <div className="flex gap-2 mt-2">
                    <PhonePrefixSelect 
                      value={formData.phoneCode} 
                      onChange={(v) => handleChange('phoneCode', v)}
                      className="w-32 input-glass"
                    />
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      className="input-glass flex-1"
                      required
                      data-testid="register-phone"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t('auth.birthDate')} *</Label>
                    <Input
                      type="date"
                      value={formData.birthDate}
                      onChange={(e) => handleChange('birthDate', e.target.value)}
                      className="input-glass mt-2"
                      required
                      data-testid="register-birthdate"
                    />
                  </div>
                  <div>
                    <Label>{t('auth.gender')} *</Label>
                    <Select value={formData.gender} onValueChange={(v) => handleChange('gender', v)}>
                      <SelectTrigger className="input-glass mt-2" data-testid="register-gender">
                        <SelectValue placeholder={t('common.select', 'Select')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">{t('auth.male')}</SelectItem>
                        <SelectItem value="female">{t('auth.female')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Minor Alert - show if under 18 */}
                {formData.birthDate && calculateAge(formData.birthDate) < 18 && (
                  <MinorAlert t={t} lang={lang} />
                )}

                {/* Consents Section - 4 checkboxes for FitNet */}
                <div className="space-y-3 pt-4 border-t border-white/10">
                  <p className="text-sm font-medium text-muted-foreground mb-2">
                    {t('auth.consents', 'Consents')}
                  </p>
                  
                  {/* Terms of Service - Required */}
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={formData.agreementTermsOfService}
                      onChange={(e) => handleChange('agreementTermsOfService', e.target.checked)}
                      className="mt-1 w-4 h-4 accent-primary shrink-0"
                      data-testid="register-terms-of-service"
                    />
                    <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                      {t('auth.iAccept', 'I accept ')}
                      <Link to={lp('document/regulamin-serwisu')} target="_blank" className="text-primary hover:underline" onClick={(e) => e.stopPropagation()}>
                        {t('auth.termsOfService', 'Terms of Service')}
                      </Link>
                      {' '}<span className="text-primary">{t('auth.requiredConsent')}</span>
                    </span>
                  </label>
                  
                  {/* Skatepark Rules - Required */}
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={formData.agreementSkateparkRules}
                      onChange={(e) => handleChange('agreementSkateparkRules', e.target.checked)}
                      className="mt-1 w-4 h-4 accent-primary shrink-0"
                      data-testid="register-skatepark-rules"
                    />
                    <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                      {t('auth.iAccept', 'I accept ')}
                      <Link to={lp('document/regulamin-skateparku')} target="_blank" className="text-primary hover:underline" onClick={(e) => e.stopPropagation()}>
                        {t('auth.skateparkRules', 'Skatepark Rules')}
                      </Link>
                      {' '}<span className="text-primary">{t('auth.requiredConsent')}</span>
                    </span>
                  </label>
                  
                  {/* Privacy Policy - Required */}
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={formData.agreementPrivacyPolicy}
                      onChange={(e) => handleChange('agreementPrivacyPolicy', e.target.checked)}
                      className="mt-1 w-4 h-4 accent-primary shrink-0"
                      data-testid="register-privacy-policy"
                    />
                    <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                      {t('auth.iAccept', 'I accept ')}
                      <Link to={lp('document/polityka-prywatnosci')} target="_blank" className="text-primary hover:underline" onClick={(e) => e.stopPropagation()}>
                        {t('auth.privacyPolicy', 'Privacy Policy')}
                      </Link>
                      {' '}<span className="text-primary">{t('auth.requiredConsent')}</span>
                    </span>
                  </label>
                  
                  {/* Marketing - Optional */}
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={formData.agreementMarketing}
                      onChange={(e) => handleChange('agreementMarketing', e.target.checked)}
                      className="mt-1 w-4 h-4 accent-primary shrink-0"
                      data-testid="register-marketing"
                    />
                    <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                      {t('auth.marketing')} <span className="text-muted-foreground/60">{t('auth.optionalConsent')}</span>
                    </span>
                  </label>
                  
                  {/* Newsletter - Optional */}
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={formData.agreementNewsletter}
                      onChange={(e) => handleChange('agreementNewsletter', e.target.checked)}
                      className="mt-1 w-4 h-4 accent-primary shrink-0"
                      data-testid="register-newsletter"
                    />
                    <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                      {t('auth.newsletterOptional', 'I want to receive newsletter with news and promotions')}
                      {' '}<span className="text-muted-foreground/60">{t('auth.optionalConsent')}</span>
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full mt-6 flex items-center justify-center gap-2"
                  data-testid="register-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>{t('auth.creatingAccount', 'Tworzenie konta...')}</span>
                    </>
                  ) : (
                    t('auth.registerBtn')
                  )}
                </button>
                
                {/* Loading info message */}
                {loading && (
                  <motion.p 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-3 text-center text-sm text-muted-foreground"
                  >
                    {t('auth.registrationInProgress', 'Trwa synchronizacja z systemem. Może to potrwać kilka sekund...')}
                  </motion.p>
                )}
              </form>

              <p className="mt-6 text-center text-sm text-muted-foreground">
                {t('auth.hasAccount')}{' '}
                <Link to={lp('login')} className="text-primary hover:underline font-medium">
                  {t('auth.login')}
                </Link>
              </p>
            </>
          ) : (
            <>
              <h1 className="text-3xl font-['Syne'] font-bold text-center mb-2">{t('auth.step2Title')}</h1>
              <p className="text-muted-foreground text-center mb-6">
                {t('auth.addChildrenOptional', 'Add children or dependents (optional)')}
              </p>
              
              {registeredUser && (
                <div className="mb-6 p-4 bg-primary/10 border border-primary/20 rounded-xl text-center">
                  <p className="text-sm text-muted-foreground mb-1">
                    {t('auth.mainAccount', 'Main account:')}
                  </p>
                  <p className="font-semibold">{registeredUser.firstName} {registeredUser.lastName}</p>
                  <p className="text-xs text-primary">FitNet ID: {registeredUser.fitnetId || registeredUser.user_id}</p>
                </div>
              )}

              {error && (
                <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm text-center">
                  {error}
                </div>
              )}

              {/* Added subaccounts */}
              {subaccounts.length > 0 && (
                <div className="space-y-3 mb-6">
                  {subaccounts.map((sub, index) => (
                    <div key={sub.subaccount_id || index} className="p-4 bg-white/5 rounded-xl flex justify-between items-center">
                      <div className="flex items-center gap-3">
                        <Users className="w-5 h-5 text-primary" />
                        <div>
                          <span className="font-medium">{sub.firstName} {sub.lastName}</span>
                          <p className="text-xs text-muted-foreground">{sub.email}</p>
                        </div>
                      </div>
                      <span className="text-xs text-green-400">
                        {t('auth.added', 'Added')}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Add new subaccount form */}
              <div className="space-y-4 p-4 border border-white/10 rounded-xl mb-6">
                <h3 className="font-semibold flex items-center gap-2">
                  <Users className="w-5 h-5" /> {t('auth.addSubaccount')}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    placeholder={t('auth.firstName')}
                    value={newSubaccount.firstName}
                    onChange={(e) => setNewSubaccount({ ...newSubaccount, firstName: e.target.value })}
                    className="input-glass"
                    data-testid="subaccount-firstname"
                  />
                  <Input
                    placeholder={t('auth.lastName')}
                    value={newSubaccount.lastName}
                    onChange={(e) => setNewSubaccount({ ...newSubaccount, lastName: e.target.value })}
                    className="input-glass"
                    data-testid="subaccount-lastname"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    type="date"
                    value={newSubaccount.birthDate}
                    onChange={(e) => setNewSubaccount({ ...newSubaccount, birthDate: e.target.value })}
                    className="input-glass"
                    data-testid="subaccount-birthdate"
                  />
                  <Select 
                    value={newSubaccount.gender} 
                    onValueChange={(v) => setNewSubaccount({ ...newSubaccount, gender: v })}
                  >
                    <SelectTrigger className="input-glass" data-testid="subaccount-gender">
                      <SelectValue placeholder={t('auth.gender')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">{t('auth.male')}</SelectItem>
                      <SelectItem value="female">{t('auth.female')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Minor Alert for subaccount */}
                {newSubaccount.birthDate && calculateAge(newSubaccount.birthDate) < 18 && (
                  <MinorAlert t={t} lang={lang} />
                )}
                
                <Input
                  type="tel"
                  placeholder={t('auth.phone')}
                  value={newSubaccount.phone}
                  onChange={(e) => setNewSubaccount({ ...newSubaccount, phone: e.target.value })}
                  className="input-glass"
                  data-testid="subaccount-phone"
                />
                <button
                  type="button"
                  onClick={addSubaccount}
                  disabled={loading}
                  className="btn-secondary w-full"
                  data-testid="add-subaccount-button"
                >
                  {loading ? t('common.loading') : t('auth.addSubaccount')}
                </button>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={handleFinish}
                  className="btn-secondary flex-1"
                  data-testid="skip-subaccounts"
                >
                  {t('auth.skip')}
                </button>
                <button
                  onClick={handleFinish}
                  className="btn-primary flex-1"
                  data-testid="finish-registration"
                >
                  {t('auth.finish', 'Finish')}
                </button>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export { LoginPage, RegisterPage };
